import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as NotebookActions from './notebook.actions';
import { NotebookClientService } from '@mode/notebooks/data-access';
import { NotebookStateService } from '../notebook-state.service';
import { AccountFacade } from '@mode/accounts/contract';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ReportFacade } from '@mode/reports/domain';
import { NotebookFacade } from '@mode/notebooks/contract';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
@Injectable()
export class NotebookEffects {
  createNotebook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookActions.createNotebook),
      mergeMap((action) =>
        this.notebookService.createNotebook(action.reportToken).pipe(
          map((res) => {
            return NotebookActions.addNotebookResourcesOnSuccess({ notebookResource: res });
          }),
          catchError((error) => of(NotebookActions.addNotebookFailure({ reportToken: action.reportToken, error })))
        )
      )
    )
  );

  refreshSessionReportRun$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotebookActions.refreshSessionReportRun),
      withLatestFrom(
        this.accountFacade.currentOrg$,
        this.reportFacade.headerReportToken$,
        this.notebookFacade.selectedReportReportRunStateSelector$,
        this.notebookFacade.selectedNotebookSession$,
        this.featureFlagFacade.asObservable(FeatureFlag.NotebookMigrationPhase1)
      ),
      filter(([, , , , migrationFlagEnable]) => migrationFlagEnable),
      switchMap(([action, account, reportToken, runState, session]) => {
        return this.notebookService
          .loadReportRun(account.username, reportToken, action.notebookToken, session.token, runState.token)
          .pipe(
            switchMap(() => EMPTY), // Don't dispatch any action on success
            catchError((err) =>
              of(
                NotebookActions.terminated({
                  terminationReason: err.message,
                  sessionToken: session.token,
                  notebookToken: action.notebookToken,
                })
              )
            )
          );
      })
    )
  );

  clearAllCellOutputs$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookActions.clearAllCellOutputs),
        tap(() => {
          this.notebookStateService.notifyOutputCleared();
        })
      ),
    { dispatch: false }
  );

  // Handle modern Angular run actions
  handleModernRun$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NotebookActions.run),
        tap((action) => {
          if (action.runAllCells) {
            // Run all cells - show all outputs
            this.notebookStateService.resetAllOutputCleared();
          } else if (action.cellTokensToRun && action.cellTokensToRun.length === 1) {
            // Single cell run - show only that cell's output
            const cellToken = action.cellTokensToRun[0];
            this.notebookStateService.resetCellOutputCleared(cellToken);
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private notebookService: NotebookClientService,
    private notebookStateService: NotebookStateService,
    private accountFacade: AccountFacade,
    private reportFacade: ReportFacade,
    private notebookFacade: NotebookFacade,
    private featureFlagFacade: FeatureFlagsFacade
  ) {}
}
